import {
  partOfDaySchema,
  type DayBreakdownData,
  type PartOfDay,
  type PartOfDayCapacity,
} from "@/api/Capacity";
import {
  categorySchema,
  isEmployeePerformedCategory,
  isVisitCategory,
  visitCategorySchema,
  type IVisitCategory,
} from "@medoma/models/activities";
import {
  medicalCompetenceSchema,
  type IMedicalCompetence,
} from "@medoma/models/shifts";
import { sumArray, sumCounts } from "@/Utils/numberUtils";
import { getIncludedShifts } from "../capacityOverviewUtils";

type CategoryCount = PartOfDayCapacity["categoryCounts"][number];

type VisitTypeCount = CategoryCount & {
  category: IVisitCategory;
  count: number;
};

type PartOfDayCellCapacity = {
  includedShiftsCount: number;
  totalStaffedHours: number;
  bookedHours: number;
  visitCounts: VisitTypeCount[];
  adminActivityCounts: CategoryCount[];
};

type BaseCellData = {
  partOfDay: PartOfDay;
  anyTimeOfDayBookedHours: number;
  anyTimeOfDayVisits: VisitTypeCount[];
  anyTimeOfDayAdmins: CategoryCount[];
  partOfDayCellCapacity: PartOfDayCellCapacity;
};

export type CellData =
  | (BaseCellData & {
      kind: "group";
      competenceGroupBreakdownData: DayBreakdownData[];
    })
  | (BaseCellData & { kind: "single" });

const emptyPartOfDayCellCapacity: PartOfDayCellCapacity = {
  includedShiftsCount: 0,
  totalStaffedHours: 0,
  bookedHours: 0,
  visitCounts: [],
  adminActivityCounts: [],
};

const nurseAndNurseAssistantCompetences = [
  medicalCompetenceSchema.enum.NurseAssistant,
  medicalCompetenceSchema.enum.NursePractitioner,
  medicalCompetenceSchema.enum.Support,
];

const doctorAndDoctorAssistantCompetences = [
  medicalCompetenceSchema.enum.MedicalDoctor,
  medicalCompetenceSchema.enum.DoctorAssistant,
];

export const groupedCompetences: IMedicalCompetence[][] = [
  nurseAndNurseAssistantCompetences,
  doctorAndDoctorAssistantCompetences,
];

const adminCategories = categorySchema.options.filter(
  (category) =>
    isEmployeePerformedCategory(category) && !isVisitCategory(category),
);

export const getVisitCounts = (
  categoryCounts: CategoryCount[],
): VisitTypeCount[] =>
  categoryCounts.filter(
    (entry): entry is VisitTypeCount =>
      isVisitCategory(entry.category) && entry.count > 0,
  );

export const getAdminCounts = (
  categoryCounts: CategoryCount[],
): CategoryCount[] =>
  categoryCounts.filter(
    (entry) =>
      adminCategories.some((category) => category === entry.category) &&
      entry.count > 0,
  );

const isEmptyPartOfDayCellCapacity = (
  capacity: PartOfDayCellCapacity | null,
): boolean => {
  if (capacity === null) {
    return true;
  }

  const isFieldEmpty: Record<keyof PartOfDayCellCapacity, boolean> = {
    includedShiftsCount:
      capacity.includedShiftsCount ===
      emptyPartOfDayCellCapacity.includedShiftsCount,
    totalStaffedHours:
      capacity.totalStaffedHours ===
      emptyPartOfDayCellCapacity.totalStaffedHours,
    bookedHours:
      capacity.bookedHours === emptyPartOfDayCellCapacity.bookedHours,
    visitCounts:
      capacity.visitCounts.length ===
      emptyPartOfDayCellCapacity.visitCounts.length,
    adminActivityCounts:
      capacity.adminActivityCounts.length ===
      emptyPartOfDayCellCapacity.adminActivityCounts.length,
  };

  return Object.values(isFieldEmpty).every(Boolean);
};

export const getCompetenceGroupBreakdownData = ({
  dayBreakdownData,
  competences,
}: {
  dayBreakdownData: DayBreakdownData[];
  competences: IMedicalCompetence[];
}): DayBreakdownData[] =>
  competences
    .map((competence) =>
      dayBreakdownData.find((data) => data.competence === competence),
    )
    .filter((data) => data !== undefined);

const toPartOfDayCellCapacity = (
  capacity: PartOfDayCapacity,
): PartOfDayCellCapacity => {
  const { bookedHours, shiftsByType, categoryCounts } = capacity;
  const includedShifts = getIncludedShifts(shiftsByType);

  return {
    includedShiftsCount: sumArray(
      includedShifts.map((shift) => shift.staffedCount),
    ),
    totalStaffedHours: sumArray(
      includedShifts.map((shift) => shift.staffedHours),
    ),
    bookedHours,
    visitCounts: getVisitCounts(categoryCounts),
    adminActivityCounts: getAdminCounts(categoryCounts),
  };
};

const aggregatePartOfDayCellCapacities = (
  partOfDayCellCapacities: PartOfDayCellCapacity[],
): PartOfDayCellCapacity => ({
  includedShiftsCount: sumArray(
    partOfDayCellCapacities.map((capacity) => capacity.includedShiftsCount),
  ),
  totalStaffedHours: sumArray(
    partOfDayCellCapacities.map((capacity) => capacity.totalStaffedHours),
  ),
  bookedHours: sumArray(
    partOfDayCellCapacities.map((capacity) => capacity.bookedHours),
  ),
  visitCounts: aggregateVisitCounts(
    partOfDayCellCapacities.flatMap((capacity) => capacity.visitCounts),
  ),
  adminActivityCounts: aggregateAdminCounts(
    partOfDayCellCapacities.flatMap((capacity) => capacity.adminActivityCounts),
  ),
});

// For several category counts where there might be several with the same category, filter out the non-visit categories and sum the counts of the same category
export const aggregateVisitCounts = (
  categoryCounts: CategoryCount[],
): VisitTypeCount[] =>
  visitCategorySchema.options
    .map((category) => ({
      category,
      count: sumCounts(
        categoryCounts.filter((entry) => entry.category === category),
      ),
    }))
    .filter(({ count }) => count > 0);

// For several category counts where there might be several with the same category, filter out the non-admin categories and sum the counts of the same category
export const aggregateAdminCounts = (
  categoryCounts: CategoryCount[],
): CategoryCount[] =>
  adminCategories
    .map((category) => ({
      category,
      count: sumCounts(
        categoryCounts.filter((entry) => entry.category === category),
      ),
    }))
    .filter(({ count }) => count > 0);

// Returns null when there is no capacity and no any time of day activities to show
const toCellData = (cellData: CellData): CellData | null =>
  isEmptyPartOfDayCellCapacity(cellData.partOfDayCellCapacity) &&
  cellData.anyTimeOfDayBookedHours === 0 &&
  cellData.anyTimeOfDayVisits.length === 0 &&
  cellData.anyTimeOfDayAdmins.length === 0
    ? null
    : cellData;

export const getAggregatedCellData = ({
  competenceGroupBreakdownData,
  partOfDay,
}: {
  competenceGroupBreakdownData: DayBreakdownData[];
  partOfDay: PartOfDay;
}): CellData | null => {
  const cellCapacitiesForCurrentDayPart = competenceGroupBreakdownData
    .map((data) => data[partOfDay])
    .filter((capacity) => capacity !== null)
    .map(toPartOfDayCellCapacity);

  const partOfDayCellCapacity = aggregatePartOfDayCellCapacities(
    cellCapacitiesForCurrentDayPart,
  );

  const anyTimeOfDayCategoryCounts =
    partOfDay === partOfDaySchema.enum.day
      ? competenceGroupBreakdownData.flatMap(
          ({ anyTimeOfDayCategoryCounts }) => anyTimeOfDayCategoryCounts,
        )
      : [];

  const anyTimeOfDayBookedHours =
    partOfDay === partOfDaySchema.enum.day
      ? sumArray(
          competenceGroupBreakdownData.map(
            (data) => data.anyTimeOfDayBookedHours,
          ),
        )
      : 0;

  const anyTimeOfDayVisits = aggregateVisitCounts(anyTimeOfDayCategoryCounts);
  const anyTimeOfDayAdmins = aggregateAdminCounts(anyTimeOfDayCategoryCounts);

  return toCellData({
    kind: "group",
    partOfDay,
    partOfDayCellCapacity,
    anyTimeOfDayBookedHours,
    anyTimeOfDayVisits,
    anyTimeOfDayAdmins,
    competenceGroupBreakdownData,
  });
};

export const getCellData = ({
  competenceBreakdownData,
  partOfDay,
}: {
  competenceBreakdownData: DayBreakdownData;
  partOfDay: PartOfDay;
}): CellData | null => {
  const rawPartOfDayCapacity = competenceBreakdownData[partOfDay];
  const partOfDayCellCapacity = rawPartOfDayCapacity
    ? toPartOfDayCellCapacity(rawPartOfDayCapacity)
    : null;

  const anyTimeOfDayCategoryCounts =
    partOfDay === partOfDaySchema.enum.day
      ? competenceBreakdownData.anyTimeOfDayCategoryCounts
      : [];

  const anyTimeOfDayBookedHours =
    partOfDay === partOfDaySchema.enum.day
      ? competenceBreakdownData.anyTimeOfDayBookedHours
      : 0;

  const anyTimeOfDayVisits = getVisitCounts(anyTimeOfDayCategoryCounts);
  const anyTimeOfDayAdmins = getAdminCounts(anyTimeOfDayCategoryCounts);

  return toCellData({
    kind: "single",
    partOfDay,
    partOfDayCellCapacity: partOfDayCellCapacity ?? emptyPartOfDayCellCapacity,
    anyTimeOfDayBookedHours,
    anyTimeOfDayVisits,
    anyTimeOfDayAdmins,
  });
};

// Estimations regarding available time:
// Lunch/break: 30-45 min per shift, an average of 0.625 hours per shift
// Administrative time: 10 minutes per home visit and clinic visit respectively
// Travel time: 25 minutes per home visit
// These estimations will probably be adjusted later based on more data and feedback.
export const calculateAvailableHours = ({
  includedShiftsCount,
  totalStaffedHours,
  bookedHours,
  visitCounts,
}: {
  includedShiftsCount: number;
  totalStaffedHours: number;
  bookedHours: number;
  visitCounts: VisitTypeCount[];
}): number => {
  const homeVisitCount =
    visitCounts.find(
      (visit) => visit.category === visitCategorySchema.enum.HomeVisit,
    )?.count ?? 0;
  const clinicVisitCount =
    visitCounts.find(
      (visit) => visit.category === visitCategorySchema.enum.ClinicVisit,
    )?.count ?? 0;

  const lunchTime = 0.625 * includedShiftsCount;
  const travelTime = 0.42 * homeVisitCount;
  const administrativeTime =
    (1 / 6) * homeVisitCount + (1 / 6) * clinicVisitCount;

  const workingHours = totalStaffedHours - lunchTime;
  const occupiedHours = bookedHours + travelTime + administrativeTime;
  const availableHours = workingHours - occupiedHours;

  // Round to nearest integer since it is an estimation, and ensure it is not negative
  return Math.max(Math.round(availableHours), 0);
};
